import type { CompanyFormValues } from "helpers/forms/abn/companyTypes";
import type { GstFormValues } from "helpers/forms/acn/abnTypes";
import { CompanyStore } from "services/company/companyStore";
import { GstLodgementFrequencyOptions, GstTurnoverOptions } from "services/company/companyTypes";
import type { GstCompanyStore } from "services/company/gstCompanyStore";
import type { CompanyBusinessActivitiesStore } from "services/companyBusinessActivities/au/companyBusinessActivitiesStore";
import { FormError } from "types/errors";

export enum CompanyAbnStepEntity {
  Number = "Number",
  BusinessDescription = "BusinessDescription",
  BusinessAddress = "BusinessAddress",
  BusinessActivity = "BusinessActivity",
  BusinessJurisdiction = "BusinessJurisdiction",
  BusinessGST = "BusinessGST",
  GSTTurnover = "GSTTurnover",
  AgriculturalOperations = "AgriculturalOperations",
}

export class CompanyAbnStepStore {
  constructor(
    private readonly _companyStore: CompanyStore,
    private readonly _companyBusinessActivitiesStore: CompanyBusinessActivitiesStore,
    private readonly _gstCompanyStore: GstCompanyStore,
  ) {}

  async updateEntity(data: CompanyFormValues, entity: CompanyAbnStepEntity): Promise<void> {
    switch (entity) {
      case CompanyAbnStepEntity.Number:
        if (!this._companyStore.company) {
          await this._companyStore.createCompany();
        }
        await this._updateAcnNumber(data);
        break;
      case CompanyAbnStepEntity.BusinessDescription:
        await this._updateBusinessDescription(data);
        break;
      case CompanyAbnStepEntity.BusinessAddress:
        await this._updateBusinessAddress(data);
        break;
      case CompanyAbnStepEntity.BusinessActivity:
        await this._updateBusinessActivity(data);
        break;
      case CompanyAbnStepEntity.BusinessJurisdiction:
        await this._updateBusinessJurisdiction(data);
        break;
      case CompanyAbnStepEntity.BusinessGST:
      case CompanyAbnStepEntity.GSTTurnover:
        await this._updateBusinessGST(data);
        break;
      case CompanyAbnStepEntity.AgriculturalOperations:
        await this._updateAgriculturalOperations(data);
        break;
    }
  }

  private async _updateAcnNumber(data: CompanyFormValues): Promise<void> {
    if (!data.acn) {
      throw new FormError("No acn number value");
    }

    await this._companyStore.updateAcnNumber(data.acn);
  }

  private async _updateBusinessJurisdiction(data: CompanyFormValues): Promise<void> {
    if (typeof data.stateOfRegistration === "undefined") {
      throw new FormError("No state jurisdiction value");
    }

    await this._companyStore.updateCompanyStateOfRegistration(data.stateOfRegistration);
  }

  private async _updateAgriculturalOperations(data: CompanyFormValues): Promise<void> {
    if (typeof data.isConductAgriculturalPropertyOperations === "undefined") {
      throw new FormError("No agricultural operations value");
    }

    await this._companyStore.updateAgriculturalPropertyOperations(data.isConductAgriculturalPropertyOperations);
  }

  private async _updateBusinessAddress(data: CompanyFormValues): Promise<void> {
    if (!data.businessAddress) {
      throw new FormError("No address selected");
    }

    await this._companyBusinessActivitiesStore.updateAddress(data.businessAddress);
  }

  private async _updateBusinessActivity(data: CompanyFormValues): Promise<void> {
    if (!data.businessActivities?.length) {
      throw new FormError("No business activities value");
    }

    const mainBusinessActivity = data.businessActivities[0];

    if (!mainBusinessActivity?.code) {
      throw new FormError("No business activity code value");
    }

    if (!data.businessAddress) {
      throw new FormError("No business activity address value");
    }

    await this._companyBusinessActivitiesStore.updateActivity(
      mainBusinessActivity.code,
      mainBusinessActivity.description,
      data.businessAddress,
    );
  }

  private async _updateBusinessDescription(data: CompanyFormValues): Promise<void> {
    await this._companyStore.updateAbnBusinessDescription(data.natureOfBusiness || "");
  }

  private async _updateBusinessGST(data: CompanyFormValues | GstFormValues): Promise<void> {
    const gstData: CompanyFormValues | GstFormValues = { ...data };

    // Transform gstTurnover from enum key to enum value
    if (data.gstTurnover && typeof data.gstTurnover === "string") {
      const turnoverKey = data.gstTurnover as keyof typeof GstTurnoverOptions;
      if (turnoverKey in GstTurnoverOptions) {
        gstData.gstTurnover = GstTurnoverOptions[turnoverKey];
      }
    }

    // Transform gstLodgementFrequency from enum key to enum value
    if (data.gstLodgementFrequency && typeof data.gstLodgementFrequency === "string") {
      const frequencyKey = data.gstLodgementFrequency as keyof typeof GstLodgementFrequencyOptions;
      if (frequencyKey in GstLodgementFrequencyOptions) {
        gstData.gstLodgementFrequency = GstLodgementFrequencyOptions[frequencyKey];
      }
    }

    await this._gstCompanyStore.updateGST(gstData as GstFormValues);
  }
}
